import { Link } from "wouter";
import { Plane, Shield } from "lucide-react";
import { useLanguage } from "@/contexts/language";

const EFFECTIVE_DATE_EN = "April 6, 2026";
const EFFECTIVE_DATE_FR = "6 avril 2026";
const supportEmail = ["support", "flightdecktrainer.ca"].join("@");
const handleSupport = () => {
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  if (isLoggedIn) { window.location.href = "/support"; }
  else { window.location.href = "mailto:" + supportEmail; }
};


export default function Privacy() {
  const { language, setLanguage } = useLanguage();
  const isFr = language === "fr";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card px-6 py-4 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="bg-primary/20 p-1.5 rounded-md">
            <Plane className="h-5 w-5 text-primary" />
          </div>
          <span className="font-bold tracking-tight text-primary font-mono">FLIGHT DECK</span>
        </Link>
        <div className="flex items-center gap-4">
          <div className="flex gap-4 text-xs font-mono text-muted-foreground">
            <Link href="/privacy" className="text-primary underline underline-offset-2">
              {isFr ? "Confidentialité" : "Privacy Policy"}
            </Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">
              {isFr ? "Conditions" : "Terms of Service"}
            </Link>
            <Link href="/waiver" className="hover:text-foreground transition-colors">
              {isFr ? "Avertissement" : "Liability Waiver"}
            </Link>
          </div>
          <button
            onClick={() => setLanguage(isFr ? "en" : "fr")}
            className="text-xs font-mono font-semibold text-primary border border-primary/40 rounded px-2 py-0.5 hover:bg-primary/10 transition-colors"
          >
            {isFr ? "EN" : "FR"}
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        {isFr ? <PrivacyFr /> : <PrivacyEn />}

        <div className="border-t border-border pt-8 flex flex-wrap gap-4 text-xs font-mono text-muted-foreground">
          <Link href="/terms" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Conditions d'utilisation" : "Terms of Service"}
          </Link>
          <Link href="/waiver" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Avertissement" : "Liability Waiver"}
          </Link>
          <a onClick={handleSupport} className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Assistance" : "Support"}
          </a>
          <Link href="/" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Retour à l'application" : "Back to App"}
          </Link>
          <span className="ml-auto">© {new Date().getFullYear()} Flight Deck CPL Exam Prep</span>
        </div>
      </div>
    </div>
  );
}

function PrivacyEn() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
        </div>
        <p className="text-sm text-muted-foreground font-mono">
          Effective Date: {EFFECTIVE_DATE_EN} &nbsp;·&nbsp; Governed by the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA), S.C. 2000, c. 5
        </p>
      </div>

      <Section title="1. Who We Are">
        <P>
          Flight Deck CPL Exam Prep ("Flight Deck," "we," "our," or "us") is an online exam preparation platform designed for candidates pursuing a Canadian Commercial Pilot Licence (CPL). We are committed to protecting your personal information in accordance with the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA) and all applicable Canadian privacy legislation.
        </P>
        <P>
          For privacy-related inquiries, please contact us at <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>.
        </P>
      </Section>

      <Section title="2. Information We Collect">
        <P>We collect personal information only to the extent necessary to provide our service. The categories of information we collect include:</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Account Information:</strong> Full name, email address, and password (stored as a secure hash — we never store your password in plain text).</li>
          <li><strong>Optional Profile Information:</strong> Home province/address, date of birth or age, gender, current pilot licence type, and ratings held. This information is entirely optional and is used only to personalize your experience.</li>
          <li><strong>Study & Performance Data:</strong> Practice session records, questions answered, answers submitted, scores, and identified weak areas.</li>
          <li><strong>Support Communications:</strong> Messages, categories, and subjects submitted through our contact form.</li>
          <li><strong>Technical Information:</strong> Standard server logs (IP address, browser type, timestamps) retained for up to 30 days solely for security and troubleshooting purposes.</li>
        </ul>
        <P>We do not collect payment information. We do not use advertising trackers, third-party analytics, or fingerprinting technologies.</P>
      </Section>

      <Section title="3. How We Use Your Information">
        <P>We use the information we collect for the following purposes:</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>To create and manage your account and authenticate your identity.</li>
          <li>To deliver exam preparation content, track your practice sessions, and identify areas for improvement.</li>
          <li>To respond to support tickets and communications you initiate.</li>
          <li>To maintain and improve the quality and accuracy of our question bank.</li>
          <li>To detect and prevent unauthorized access or abuse of the platform.</li>
        </ul>
        <P>We do not use your personal information for automated decision-making that produces legal or similarly significant effects.</P>
      </Section>

      <Section title="4. Consent">
        <P>
          By creating an account, you expressly consent to the collection, use, and disclosure of your personal information as described in this Privacy Policy. Where we collect optional information, you may choose not to provide it — this will not affect your access to the core service.
        </P>
        <P>
          You may withdraw your consent at any time by emailing us at <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. Withdrawal of consent may affect our ability to continue providing the service.
        </P>
      </Section>

      <Section title="5. Disclosure of Your Information">
        <P>
          We do not sell, rent, or trade your personal information. We do not share your personal information with third parties except in the following limited circumstances:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Service Providers:</strong> We use infrastructure and hosting providers (including database and server hosting) who process data solely on our behalf and under contractual obligations consistent with this Policy.</li>
          <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, court order, or other legal process, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</li>
          <li><strong>Business Transfer:</strong> If Flight Deck is acquired or merges with another entity, your information may be transferred as part of that transaction. You will be notified in advance.</li>
        </ul>
      </Section>

      <Section title="6. Data Retention">
        <P>
          We retain your personal information for as long as your account is active, or as needed to provide the service. If you request deletion of your account, we will delete or anonymize your personal information within 30 days, except where we are required by law to retain certain records.
        </P>
        <P>
          Study performance data may be retained in aggregated, anonymized form for the purpose of improving the question bank. Anonymized data cannot be linked back to you.
        </P>
      </Section>

      <Section title="7. Security">
        <P>
          We implement industry-standard technical and organizational measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These include:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Passwords are hashed using Argon2id, a memory-hard cryptographic algorithm.</li>
          <li>All data transmission is encrypted using TLS.</li>
          <li>Access to user data is restricted to authorized personnel only.</li>
        </ul>
        <P>
          While we take all reasonable steps to protect your information, no method of electronic storage or transmission is 100% secure. You are responsible for maintaining the confidentiality of your account credentials.
        </P>
      </Section>

      <Section title="8. Your Rights Under PIPEDA">
        <P>Under PIPEDA, you have the following rights with respect to your personal information:</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Right of Access:</strong> You may request access to the personal information we hold about you.</li>
          <li><strong>Right to Correction:</strong> You may request that we correct inaccurate or incomplete information.</li>
          <li><strong>Right to Withdraw Consent:</strong> You may withdraw your consent to the collection and use of your personal information at any time, subject to legal and contractual restrictions.</li>
          <li><strong>Right to Deletion:</strong> You may request the deletion of your account and associated personal information.</li>
          <li><strong>Right to Complain:</strong> If you believe we have handled your personal information improperly, you may file a complaint with the <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">Office of the Privacy Commissioner of Canada</a>.</li>
        </ul>
        <P>
          To exercise any of these rights, please email us at <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. We will respond within 30 days.
        </P>
      </Section>

      <Section title="9. Cookies and Local Storage">
        <P>
          We use a minimal session cookie ("cpl_session") to maintain your authenticated session. This cookie is HttpOnly, Secure, and is not accessible to third-party scripts. We do not use advertising cookies, tracking pixels, or third-party analytics cookies.
        </P>
        <P>
          We also use browser localStorage to store your authentication token client-side for session continuity. No personal information beyond your session token is stored locally.
        </P>
      </Section>

      <Section title="10. Children's Privacy">
        <P>
          This service is not directed to individuals under the age of 16. We do not knowingly collect personal information from minors. If you believe a minor has provided us personal information, please contact us and we will delete it promptly.
        </P>
      </Section>

      <Section title="11. Scenario Training — Data Handling">
        <P>
          When you use Scenario Training (if available to your plan), we process the same categories of information described elsewhere in this policy — for example, session mode, question identifiers, your answers, and timestamps — on our own systems to deliver the feature.
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>On-platform processing:</strong> Scenario sessions are handled like other practice sessions. We do not send your name, email address, or performance history to third-party large-language-model providers for real-time question generation.</li>
          <li><strong>Plan restrictions:</strong> Scenario Training may be unavailable or limited depending on your subscription.</li>
          <li><strong>Changes:</strong> If we materially change how scenario data is handled, we will update this Privacy Policy and, where appropriate, notify you.</li>
          <li><strong>Opt-out:</strong> You can avoid Scenario Training entirely by not selecting that mode when starting a session.</li>
        </ul>
      </Section>

      <Section title="12. Changes to This Policy">
        <P>
          We may update this Privacy Policy from time to time. When we make material changes, we will notify you by email or by posting a prominent notice on the platform. Your continued use of the service after any changes constitutes acceptance of the revised Policy. The effective date at the top of this page reflects the most recent revision.
        </P>
      </Section>

      <Section title="13. Contact Us">
        <P>
          For any questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact our Privacy Officer at:
        </P>
        <div className="bg-card border border-border rounded-md p-4 font-mono text-xs space-y-1">
          <div className="font-semibold text-foreground">Flight Deck CPL Exam Prep — Privacy Officer</div>
          <div className="text-muted-foreground">Contact: <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a></div>
        </div>
      </Section>
    </>
  );
}

function PrivacyFr() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Politique de confidentialité</h1>
        </div>
        <p className="text-sm text-muted-foreground font-mono">
          Date d'entrée en vigueur : {EFFECTIVE_DATE_FR} &nbsp;·&nbsp; Régie par la <em>Loi sur la protection des renseignements personnels et les documents électroniques</em> (LPRPDE), L.C. 2000, ch. 5
        </p>
      </div>

      <Section title="1. Qui sommes-nous">
        <P>
          Flight Deck CPL Exam Prep (« Flight Deck », « nous », « notre » ou « nos ») est une plateforme de préparation aux examens en ligne conçue pour les candidats qui souhaitent obtenir une licence de pilote commercial canadienne (LPC). Nous nous engageons à protéger vos renseignements personnels conformément à la <em>Loi sur la protection des renseignements personnels et les documents électroniques</em> (LPRPDE) et à toute autre loi canadienne applicable sur la protection de la vie privée.
        </P>
        <P>
          Pour toute demande concernant la protection de la vie privée, veuillez nous contacter à l'adresse <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>.
        </P>
      </Section>

      <Section title="2. Renseignements que nous recueillons">
        <P>Nous recueillons des renseignements personnels uniquement dans la mesure nécessaire à la prestation de notre service. Les catégories de renseignements recueillis comprennent :</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Informations de compte :</strong> Nom complet, adresse courriel et mot de passe (conservé sous forme de hachage sécurisé — nous ne stockons jamais votre mot de passe en clair).</li>
          <li><strong>Informations de profil facultatives :</strong> Province ou adresse de résidence, date de naissance ou âge, genre, type de licence de pilote actuelle et qualifications détenues. Ces renseignements sont entièrement facultatifs et ne sont utilisés qu'à des fins de personnalisation de votre expérience.</li>
          <li><strong>Données d'étude et de performance :</strong> Dossiers de sessions de pratique, questions répondues, réponses soumises, résultats et zones de faiblesse identifiées.</li>
          <li><strong>Communications d'assistance :</strong> Messages, catégories et sujets soumis via notre formulaire de contact.</li>
          <li><strong>Informations techniques :</strong> Journaux de serveur standard (adresse IP, type de navigateur, horodatages) conservés jusqu'à 30 jours uniquement à des fins de sécurité et de dépannage.</li>
        </ul>
        <P>Nous ne recueillons pas de renseignements de paiement. Nous n'utilisons pas de traceurs publicitaires, d'analyses de tiers, ni de technologies d'empreinte numérique.</P>
      </Section>

      <Section title="3. Utilisation de vos renseignements">
        <P>Nous utilisons les renseignements recueillis aux fins suivantes :</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Créer et gérer votre compte et vérifier votre identité.</li>
          <li>Fournir le contenu de préparation aux examens, suivre vos sessions de pratique et identifier les domaines à améliorer.</li>
          <li>Répondre aux billets d'assistance et aux communications que vous initiez.</li>
          <li>Maintenir et améliorer la qualité et l'exactitude de notre banque de questions.</li>
          <li>Détecter et prévenir l'accès non autorisé ou l'abus de la plateforme.</li>
        </ul>
        <P>Nous n'utilisons pas vos renseignements personnels à des fins de prise de décision automatisée produisant des effets juridiques ou similaires.</P>
      </Section>

      <Section title="4. Consentement">
        <P>
          En créant un compte, vous consentez expressément à la collecte, à l'utilisation et à la divulgation de vos renseignements personnels tels que décrits dans la présente Politique de confidentialité. Lorsque nous recueillons des renseignements facultatifs, vous pouvez choisir de ne pas les fournir — cela n'affectera pas votre accès au service principal.
        </P>
        <P>
          Vous pouvez retirer votre consentement à tout moment en nous écrivant à <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. Le retrait du consentement peut affecter notre capacité à continuer à vous fournir le service.
        </P>
      </Section>

      <Section title="5. Divulgation de vos renseignements">
        <P>
          Nous ne vendons, ne louons ni n'échangeons vos renseignements personnels. Nous ne partageons vos renseignements personnels avec des tiers que dans les circonstances limitées suivantes :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Fournisseurs de services :</strong> Nous faisons appel à des fournisseurs d'infrastructure et d'hébergement (y compris l'hébergement de bases de données et de serveurs) qui traitent les données uniquement en notre nom et en vertu d'obligations contractuelles conformes à la présente Politique.</li>
          <li><strong>Exigences légales :</strong> Nous pouvons divulguer vos renseignements si la loi, une ordonnance judiciaire ou tout autre processus légal l'exige, ou si nous estimons que la divulgation est nécessaire pour protéger nos droits, votre sécurité ou celle d'autrui.</li>
          <li><strong>Transfert d'entreprise :</strong> Si Flight Deck est acquis ou fusionne avec une autre entité, vos renseignements peuvent être transférés dans le cadre de cette transaction. Vous en serez informé à l'avance.</li>
        </ul>
      </Section>

      <Section title="6. Conservation des données">
        <P>
          Nous conservons vos renseignements personnels aussi longtemps que votre compte est actif ou que nécessaire pour vous fournir le service. Si vous demandez la suppression de votre compte, nous supprimerons ou anonymiserons vos renseignements personnels dans les 30 jours, sauf dans les cas où la loi nous oblige à conserver certains dossiers.
        </P>
        <P>
          Les données de performance d'étude peuvent être conservées sous une forme agrégée et anonymisée afin d'améliorer la banque de questions. Les données anonymisées ne peuvent pas être reliées à votre identité.
        </P>
      </Section>

      <Section title="7. Sécurité">
        <P>
          Nous mettons en œuvre des mesures techniques et organisationnelles conformes aux normes de l'industrie pour protéger vos renseignements personnels contre tout accès, divulgation, modification ou destruction non autorisés. Ces mesures comprennent :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Les mots de passe sont hachés avec Argon2id, un algorithme cryptographique à mémoire intense.</li>
          <li>Toutes les transmissions de données sont chiffrées via TLS.</li>
          <li>L'accès aux données des utilisateurs est restreint au personnel autorisé uniquement.</li>
        </ul>
        <P>
          Bien que nous prenions toutes les mesures raisonnables pour protéger vos renseignements, aucun mode de stockage ou de transmission électronique n'est sécurisé à 100 %. Vous êtes responsable de la confidentialité de vos identifiants de connexion.
        </P>
      </Section>

      <Section title="8. Vos droits en vertu de la LPRPDE">
        <P>En vertu de la LPRPDE, vous disposez des droits suivants concernant vos renseignements personnels :</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Droit d'accès :</strong> Vous pouvez demander l'accès aux renseignements personnels que nous détenons à votre sujet.</li>
          <li><strong>Droit de rectification :</strong> Vous pouvez demander que nous corrigions les renseignements inexacts ou incomplets.</li>
          <li><strong>Droit de retrait du consentement :</strong> Vous pouvez retirer votre consentement à la collecte et à l'utilisation de vos renseignements personnels à tout moment, sous réserve des restrictions légales et contractuelles.</li>
          <li><strong>Droit à la suppression :</strong> Vous pouvez demander la suppression de votre compte et des renseignements personnels associés.</li>
          <li><strong>Droit de déposer une plainte :</strong> Si vous estimez que nous avons traité vos renseignements personnels de manière inappropriée, vous pouvez déposer une plainte auprès du <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">Commissariat à la protection de la vie privée du Canada</a>.</li>
        </ul>
        <P>
          Pour exercer l'un de ces droits, veuillez nous écrire à <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a>. Nous répondrons dans un délai de 30 jours.
        </P>
      </Section>

      <Section title="9. Témoins et stockage local">
        <P>
          Nous utilisons un témoin de session minimal (« cpl_session ») pour maintenir votre session authentifiée. Ce témoin est HttpOnly, Sécurisé et n'est pas accessible aux scripts tiers. Nous n'utilisons pas de témoins publicitaires, de pixels de suivi ni de témoins d'analyse tiers.
        </P>
        <P>
          Nous utilisons également le stockage local du navigateur (localStorage) pour conserver votre jeton d'authentification côté client afin d'assurer la continuité de la session. Aucun renseignement personnel autre que votre jeton de session n'est stocké localement.
        </P>
      </Section>

      <Section title="10. Vie privée des enfants">
        <P>
          Ce service ne s'adresse pas aux personnes de moins de 16 ans. Nous ne recueillons pas sciemment de renseignements personnels auprès de mineurs. Si vous pensez qu'un mineur nous a fourni des renseignements personnels, veuillez nous contacter et nous les supprimerons promptement.
        </P>
      </Section>

      <Section title="11. Entraînement par scénarios — Traitement des données">
        <P>
          Lorsque vous utilisez l'entraînement par scénarios (si disponible dans votre abonnement), nous traitons les mêmes catégories de renseignements décrites ailleurs dans la présente politique — par exemple le mode de session, les identifiants de questions, vos réponses et les horodatages — sur nos propres systèmes pour fournir la fonctionnalité.
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Traitement sur la plateforme :</strong> Les sessions de scénario sont gérées comme les autres sessions de pratique. Nous n'envoyons pas votre nom, votre adresse courriel ni votre historique de performance à des fournisseurs tiers de grands modèles de langage pour générer des questions en temps réel.</li>
          <li><strong>Restrictions de forfait :</strong> L'entraînement par scénarios peut être indisponible ou limité selon votre abonnement.</li>
          <li><strong>Modifications :</strong> Si nous modifions de façon importante le traitement des données de scénario, nous mettrons à jour la présente Politique de confidentialité et, le cas échéant, vous en informerons.</li>
          <li><strong>Option de retrait :</strong> Vous pouvez éviter entièrement l'entraînement par scénarios en ne sélectionnant pas ce mode au démarrage d'une session.</li>
        </ul>
      </Section>

      <Section title="12. Modifications de la présente Politique">
        <P>
          Nous pouvons mettre à jour la présente Politique de confidentialité de temps à autre. Lorsque nous apportons des modifications importantes, nous vous en informerons par courriel ou par l'affichage d'un avis bien visible sur la plateforme. Votre utilisation continue du service après tout changement constitue votre acceptation de la Politique révisée. La date d'entrée en vigueur en haut de cette page reflète la révision la plus récente.
        </P>
      </Section>

      <Section title="13. Nous joindre">
        <P>
          Pour toute question, préoccupation ou demande concernant la présente Politique de confidentialité ou vos renseignements personnels, veuillez contacter notre responsable de la protection de la vie privée à :
        </P>
        <div className="bg-card border border-border rounded-md p-4 font-mono text-xs space-y-1">
          <div className="font-semibold text-foreground">Flight Deck CPL Exam Prep — Responsable de la protection de la vie privée</div>
          <div className="text-muted-foreground">Contact : <a onClick={handleSupport} className="text-primary underline underline-offset-2">Support</a></div>
        </div>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-bold text-foreground border-b border-border pb-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-foreground/80 leading-relaxed">{children}</p>;
}
